/*
 * Public contract signing page — routed at /tp/contract/:token
 * The admin mints a token via POST /api/tpt/providers/{tp_id}/contract/send-link
 * which emails the TP owner a link to this page. No login required — token IS auth.
 */
import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import axios from "axios";
import { SignaturePad } from "../components/SignaturePad";
import PageBackLink from "../components/PageBackLink";
import { ShieldCheck, CheckCircle2, AlertTriangle } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

export default function TPContractSign() {
  const { token } = useParams();
  const [state, setState] = useState({ loading: true });
  const [signerName, setSignerName] = useState("");
  const [signerTitle, setSignerTitle] = useState("");
  const [signerEmail, setSignerEmail] = useState("");
  const [sigDataUrl, setSigDataUrl] = useState(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    axios.get(`${API}/api/tpt/contract/public/${token}`)
      .then(({ data }) => setState({ loading: false, ok: true, data }))
      .catch((e) => setState({ loading: false, ok: false, error: e?.response?.data?.detail || "Invalid link" }));
  }, [token]);

  const submit = async () => {
    if (!signerName.trim()) { toast.error("Please enter the signer's printed name"); return; }
    if (!sigDataUrl) { toast.error("Please sign in the box above"); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/api/tpt/contract/public/${token}`, {
        signer_name: signerName.trim(),
        signer_title: signerTitle.trim() || null,
        signer_email: signerEmail.trim() || null,
        signature_data_url: sigDataUrl,
        contract_version: state.data?.contract_version || "v1",
      });
      setDone(true);
      toast.success("Contract signed — thank you!");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Signing failed");
    } finally { setBusy(false); }
  };

  if (state.loading) {
    return (
      <div style={{ minHeight: "100vh", background: "#F9F8F6", display: "grid", placeItems: "center", fontFamily: "Helvetica,Arial,sans-serif" }}>
        <div style={{ color: "#64748B" }}>Loading contract…</div>
      </div>
    );
  }
  if (!state.ok) {
    return (
      <div style={{ minHeight: "100vh", background: "#F9F8F6", display: "grid", placeItems: "center", padding: 24, fontFamily: "Helvetica,Arial,sans-serif" }}>
        <div style={{ maxWidth: 460, background: "#fff", borderRadius: 16, padding: 32, textAlign: "center", boxShadow: "0 4px 24px rgba(15,23,42,0.06)" }}>
          <AlertTriangle style={{ color: "#C55A44", margin: "0 auto 12px" }} size={40}/>
          <h1 style={{ color: "#0F172A", marginBottom: 8 }}>Link not valid</h1>
          <p style={{ color: "#64748B", fontSize: 14 }}>{state.error}</p>
        </div>
      </div>
    );
  }
  if (done || state.data.already_signed) {
    return (
      <div style={{ minHeight: "100vh", background: "#F9F8F6", display: "grid", placeItems: "center", padding: 24, fontFamily: "Helvetica,Arial,sans-serif" }}>
        <div style={{ maxWidth: 480, background: "#fff", borderRadius: 16, padding: 32, textAlign: "center", boxShadow: "0 4px 24px rgba(15,23,42,0.06)" }} data-testid="contract-signed-screen">
          <CheckCircle2 style={{ color: "#2B4C3B", margin: "0 auto 12px" }} size={44}/>
          <h1 style={{ color: "#2B4C3B", marginBottom: 6 }}>Contract signed</h1>
          <p style={{ color: "#64748B", fontSize: 14 }}>
            Thanks — {state.data.tp_name}&apos;s Transportation Provider Agreement is on file. A copy will be emailed to you shortly. You can close this window.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: "100vh", background: "#F9F8F6", padding: 24, fontFamily: "Helvetica,Arial,sans-serif" }}>
      <PageBackLink fallbackTo="/" label="Back to home" style={{ padding: "0 0 16px" }} />
      <div style={{ maxWidth: 720, margin: "0 auto", background: "#fff", borderRadius: 20, padding: 32, boxShadow: "0 4px 24px rgba(15,23,42,0.06)" }} data-testid="contract-sign-page">
        <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 4 }}>
          <ShieldCheck style={{ color: "#2B4C3B" }} size={28}/>
          <h1 style={{ color: "#2B4C3B", fontSize: 24, margin: 0 }}>MedexOVA TP Agreement</h1>
        </div>
        <p style={{ color: "#64748B", fontSize: 13, marginTop: 4 }}>
          Signing on behalf of <b style={{ color: "#0F172A" }}>{state.data.tp_name}</b>
        </p>

        <div style={{ marginTop: 24, padding: 20, background: "#F1EFE8", borderRadius: 12, fontSize: 13.5, lineHeight: 1.6, color: "#0F172A", maxHeight: 320, overflowY: "auto" }}>
          <h3 style={{ marginTop: 0, color: "#2B4C3B" }}>Transportation Provider Agreement (v1)</h3>
          <p>
            This Agreement is entered into between Medex Transportation Services LLC (&ldquo;MedexOVA&rdquo;) and {state.data.tp_name} (&ldquo;Provider&rdquo;),
            effective on the date signed below. Provider agrees to:
          </p>
          <ol>
            <li>Maintain active commercial auto insurance ≥ $1M CSL and provide a current Certificate of Insurance naming MedexOVA as certificate holder.</li>
            <li>Ensure all drivers dispatched under this contract hold a valid state license, chauffeur endorsement (where required), and clear MedexOva credentialing at all times.</li>
            <li>Complete assigned trips within MedexOVA&apos;s on-time SLAs (contract rules configurable per Provider) and accept SLA-based scorecard rating.</li>
            <li>Report any accident, cancellation, or no-show promptly through the MedexOVA portal or driver app; upload Proof of Delivery (POD) including signature, geo, and photo where required.</li>
            <li>Comply with MedexOVA&apos;s HIPAA privacy addendum and refrain from disclosing any Protected Health Information (PHI) outside the scope of trip execution.</li>
            <li>Accept invoicing terms as displayed in each issued invoice (net 30 days from receipt).</li>
          </ol>
          <p style={{ fontSize: 12, color: "#64748B" }}>
            This contract may be terminated by either party with 30 days&apos; written notice. Full terms available at
            <a href="https://medexova.work/legal/tp-agreement" style={{ color: "#2B4C3B", marginLeft: 4 }}>medexova.work/legal/tp-agreement</a>.
          </p>
        </div>

        <div style={{ marginTop: 24 }}>
          <label style={{ display: "block", marginBottom: 12 }}>
            <div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: "0.12em", color: "#64748B", marginBottom: 4 }}>Signer name *</div>
            <input value={signerName} onChange={(e) => setSignerName(e.target.value)} required data-testid="signer-name"
              style={{ width: "100%", padding: "10px 12px", border: "1px solid #E2E0D8", borderRadius: 8, fontSize: 14 }}/>
          </label>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 12 }}>
            <label>
              <div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: "0.12em", color: "#64748B", marginBottom: 4 }}>Title</div>
              <input value={signerTitle} onChange={(e) => setSignerTitle(e.target.value)} placeholder="e.g., Owner, Operations Manager" data-testid="signer-title"
                style={{ width: "100%", padding: "10px 12px", border: "1px solid #E2E0D8", borderRadius: 8, fontSize: 14 }}/>
            </label>
            <label>
              <div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: "0.12em", color: "#64748B", marginBottom: 4 }}>Email</div>
              <input type="email" value={signerEmail} onChange={(e) => setSignerEmail(e.target.value)} data-testid="signer-email"
                style={{ width: "100%", padding: "10px 12px", border: "1px solid #E2E0D8", borderRadius: 8, fontSize: 14 }}/>
            </label>
          </div>

          <div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: "0.12em", color: "#64748B", marginBottom: 4 }}>Signature *</div>
          <SignaturePad onChange={setSigDataUrl} height={200} ariaLabel="Contract signature"/>

          <button
            type="button"
            onClick={submit}
            disabled={busy}
            data-testid="contract-submit"
            style={{
              marginTop: 24, background: "#2B4C3B", color: "#fff", border: "none",
              padding: "14px 24px", borderRadius: 10, fontSize: 15, fontWeight: 700, cursor: "pointer", opacity: busy ? 0.6 : 1,
              width: "100%",
            }}
          >
            {busy ? "Signing…" : "Sign contract"}
          </button>
          <div style={{ marginTop: 12, fontSize: 11, color: "#94a3b8", textAlign: "center" }}>
            By clicking Sign, you confirm the information above is accurate and agree to the terms above.
          </div>
        </div>
      </div>
    </div>
  );
}
